<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";

import {
  getDate,
  getDateAndHour,
  getDifference,
  getDiffBreaks,
} from "@/common/utils/dates";
import { paginateText, getMarkdownBlockHeights } from "@/common/utils/text";

import { fileGet } from "@/common/api/rest/files";

import type { AnalysisFullType } from "@/common/types/analysis";

import { pageExport } from "@/components";

interface ContentType {
  title: string | null;
  type: string;
  break: boolean;
  content: any;
}

interface PageType {
  page: number;
  content: {
    [key: string]: ContentType | null;
  } | null;
}

const props = defineProps<{
  data: AnalysisFullType;
  width: string;
  scale: number;
  footer?: any;
  filters?: string[];
}>();

const { t, tm } = useI18n();

const width = ref("210mm");
// Altura útil da página A4 (297mm - margens) convertida para pixels aproximados
// Considerando ~3.78 pixels por mm: 297mm = ~1122px, menos margens e footer
const height = ref(950); // Aumentado para melhor aproveitamento
const heightTitle = ref(50); // Reduzido para economizar espaço
const minSpaceForContent = ref(100); // Espaço mínimo necessário para iniciar novo conteúdo

const pages = ref<PageType[]>([]);

const summary = computed(
  () => tm("report.types.summary") as Record<string, string>,
);

async function paginate() {
  if (!props.data) return;

  pages.value = [];
  let PAGE = 0;
  let HEIGHT_INTERNAL = 0;

  pages.value.push({
    page: PAGE,
    content: {},
  });

  const paginateTitle = () => {
    let title = "";

    title += `<strong>${t("report.labels.evaluation").toUpperCase()}:</strong>`;
    title += `&nbsp; ${props.data.evaluation.code}`;
    title += "&nbsp; &nbsp; &nbsp; &nbsp;";
    title += `<strong>${t("report.labels.analyse").toUpperCase()}:</strong>`;
    title += `&nbsp; ${props.data.code}`;

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      title: {
        title,
        type: "TITLE",
        break: false,
        content: "",
      },
    };

    HEIGHT_INTERNAL += heightTitle.value;
  };

  const newPage = (field: string, type: string, title: string | null) => {
    PAGE++;
    pages.value[PAGE] = {
      page: PAGE,
      content: {},
    };
    HEIGHT_INTERNAL = 0;
    paginateTitle();

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      [field]: {
        title,
        type,
        break: false,
        content: "",
      },
    };
  };

  const hasSpaceForContent = (requiredHeight: number): boolean => {
    return HEIGHT_INTERNAL + requiredHeight <= height.value;
  };

  const paginateCompany = () => {
    if (!props.data?.company) return;

    const HEITHG_COMPANY_AVATAR = 90;
    const COMPANY_MARGIN = 20; // Margem de segurança

    const {
      avatar,
      name,
      document_name,
      document_number,
      address: addresses,
      address_id,
      contacts: contactsAll,
      contact_id,
    } = props.data!.company || {};

    // Calcular altura total necessária
    const companyNameInfos = paginateText(name?.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    let documentText = "";
    if (document_name) documentText += document_name;
    if (document_number) documentText += ` ${document_number}`;

    const companyDocumentInfos = paginateText(documentText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    const address = addresses?.filter(
      (item) => item.address_id === address_id,
    )?.[0];

    let addressText = "";
    if (address?.address) addressText += address.address;
    if (address?.number) addressText += `, ${address.number}`;
    if (address?.complement) addressText += `, ${address.complement}`;
    if (address?.district) addressText += `, ${address.district}`;
    if (address?.city) addressText += `, ${address.city}`;
    if (address?.state) addressText += `, ${address.state}`;
    if (address?.country) addressText += `, ${address.country}`;
    if (address?.cep) addressText += `, CEP ${address.cep}`;

    const companyAdressInfos = paginateText(addressText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const contacts = contactsAll?.filter(
      (item) => item.contact_id === contact_id,
    )?.[0];

    let contactText = "";
    if (contacts?.name) contactText += contacts.name;
    if (contacts?.contact) contactText += ` - ${contacts.contact}`;

    const companyContactInfos = paginateText(contactText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const companyNameHeight = companyNameInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyDocumentHeight = companyDocumentInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyAdressHeight = companyAdressInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyContactHeight = companyContactInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const HEITHG_COMPANY_INFOS =
      companyNameHeight +
      companyDocumentHeight +
      companyAdressHeight +
      companyContactHeight +
      12; // gaps entre linhas

    const HEIGHT_TOTAL =
      Math.max(HEITHG_COMPANY_AVATAR, HEITHG_COMPANY_INFOS) + COMPANY_MARGIN;

    // Verificar se há espaço suficiente
    if (!hasSpaceForContent(heightTitle.value + HEIGHT_TOTAL)) {
      newPage("company", "COMPANY", null);
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
    }

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      company: {
        title: null,
        type: "COMPANY",
        break: false,
        content: {
          avatar: avatar,
          name: companyNameInfos[0]?.text,
          document: companyDocumentInfos[0]?.text,
          address: companyAdressInfos[0]?.text,
          contact: companyContactInfos[0]?.text,
        },
      },
    };

    HEIGHT_INTERNAL += HEIGHT_TOTAL;
  };

  const paginateAnalyse = () => {
    if (!props.data) return;

    const analyse = props.data;

    let markText = "";

    markText += `#### ${t("report.labels.evaluation").toUpperCase()} ${analyse.evaluation.code}`;
    markText += ` &nbsp; | &nbsp; ${t("report.labels.analyse").toUpperCase()} ${analyse.code}\n`;
    markText += `${analyse.description ?? ""}\n\n`;

    markText += `- **${t("report.labels.status")}:** &nbsp; ${t(`enums.analysis_status.${analyse.status}`).toUpperCase()}\n`;

    if (analyse.start_date) {
      const diffTime = getDiffBreaks(
        analyse.start_edited ?? analyse.start_date,
        analyse.end_date
          ? (analyse.end_edited ?? analyse.end_date)
          : new Date().toISOString(),
        analyse.breaks ?? [],
      );
      markText += `- **${t("report.labels.start")}:** &nbsp; ${getDateAndHour(analyse.start_edited ?? analyse.start_date)}`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; ${getDateAndHour(analyse.end_edited ?? analyse.end_date)}`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** &nbsp; ${diffTime.text}\n`;
    } else {
      markText += `- **${t("report.labels.start")}:** &nbsp; --/--/---- --:--:--`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; --/--/---- --:--:--`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** 0m 0s\n`;
    }

    if (analyse.technical) {
      markText += `- **${t("report.labels.technical")}:**`;
      markText += ` &nbsp; ${analyse.technical.name}\n`;
    }

    if (analyse.assistant) {
      if (analyse.technical) {
        markText += "&nbsp; | &nbsp; ";
      } else {
        markText += "- ";
      }

      markText += `**${t("report.labels.assistant")}:**`;
      markText += ` &nbsp; ${analyse.assistant.name}\n`;
    } else {
      markText += "\n";
    }

    markText += `- **${t("report.labels.degree_of_risk")}:** &nbsp; ${analyse.danger}\n`;

    if (analyse.status === "CANCELED") {
      markText += `#### ${t("report.labels.cancellation")}\n`;
      markText += `- **${t("report.labels.date")}:** &nbsp; ${getDateAndHour(analyse.canceled_date)} \n`;
      markText += `- **${t("report.labels.motive")}:** &nbsp; ${analyse.canceled_description ?? ""}\n`;
    }

    if (analyse.breaks?.length) {
      markText += "---\n";
      markText += `#### ${t("report.labels.pauses").toUpperCase()}\n\n`;
      analyse.breaks.forEach((item, index) => {
        const pauseDiffTime = getDifference(
          item.start_date,
          item.end_date ? item.end_date : new Date().toISOString(),
        );

        markText += `&nbsp; &nbsp; **${index + 1} - ${item.motive}**\n\n`;
        if (item.description) {
          markText += `&nbsp; &nbsp; ${item.description}\n`;
        }
        markText += `- **${t("report.labels.start")}:** &nbsp; ${getDateAndHour(item.start_date)}`;
        markText += ` &nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; ${getDateAndHour(item.end_date)}`;
        markText += ` &nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** &nbsp; ${pauseDiffTime.text}\n`;

        if ((analyse.breaks?.length ?? 0) > index + 1) {
          markText += "---\n";
        }
      });
    }

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("analyse", "ANALYSE", t("report.titles.analyse"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        analyse: {
          title: t("report.titles.analyse"),
          type: "ANALYSE",
          break: false,
          content: "",
        },
      };
    }

    let pageText = "";
    const lines = markText.split("\n");

    for (let i = 0; i < lines.length; i++) {
      const text = lines[i];
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 3; // margem entre linhas

      if (!hasSpaceForContent(blockHeight)) {
        if (pageText.trim()) {
          pages.value[PAGE]!.content!.analyse!.content = marked(pageText);
        }
        newPage("analyse", "ANALYSE", null);
        pageText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        pageText += `${text}\n`;
      }
    }

    if (pageText.trim()) {
      pages.value[PAGE]!.content!.analyse!.content = marked(pageText);
    }
  };

  const paginateEmployee = () => {
    if (!props.data?.employee) return;

    const employee = props.data.employee;

    let markText = "";

    if (employee.social_name) {
      markText += `#### ${employee.social_name}\n`;
    } else {
      markText += `#### ${employee.name}\n`;
    }
    markText += `${employee.document_name?.toUpperCase()} ${employee.document_number ?? ""}`;
    if (employee.registration_number) {
      markText += ` &nbsp; | &nbsp; ${t("report.labels.registration").toUpperCase()}: &nbsp; ${employee.registration_number ?? ""}`;
    }
    markText += "\n\n";

    markText += `${employee.description ?? ""}\n`;

    markText += `- **${t("report.labels.role")}:** &nbsp; ${employee.role ?? ""}\n`;
    markText += `- **${t("report.labels.sector")}:** &nbsp;  ${employee.sector ?? ""}\n`;

    if (employee.start_date) {
      markText += `- **${t("report.labels.bond")}:**`;
      markText += ` &nbsp; ${getDate(employee.start_date)}`;

      if (employee.end_date) {
        markText += ` - ${getDate(employee.end_date)}\n`;
      } else {
        markText += ` - ${t("report.labels.current")} \n`;
      }
    }

    if (employee.ges_name) {
      markText += `- **${t("report.labels.ges_name")}:**`;
      markText += ` &nbsp;  ${employee.ges_name ?? ""}`;
      markText += "\n";
    }

    if (employee.ges_status) {
      markText += `- **${t("report.labels.ges_status")}:**`;
      markText += `  &nbsp;  ${employee.ges_status ? t("common.labels." + employee.ges_status?.toLocaleLowerCase()) : ""}`;
      markText += "\n";
    }

    if (employee.ges_description) {
      markText += `- **${t("report.labels.ges_description")}:**`;
      markText += ` &nbsp; ${employee.ges_description ?? ""}\n`;
    }

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("employee", "EMPLOYEE", t("report.titles.employee"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        employee: {
          title: t("report.titles.employee"),
          type: "EMPLOYEE",
          break: false,
          content: "",
        },
      };
    }

    let pageText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 3;

      if (!hasSpaceForContent(blockHeight)) {
        if (pageText.trim()) {
          pages.value[PAGE]!.content!.employee!.content = marked(pageText);
        }
        newPage("employee", "EMPLOYEE", null);
        pageText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        pageText += `${text}\n`;
      }
    });

    if (pageText.trim()) {
      pages.value[PAGE]!.content!.employee!.content = marked(pageText);
    }
  };

  const paginateMethod = () => {
    if (!props.data?.methods?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("methods", "METHODS", t("report.titles.methods"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        methods: {
          title: t("report.titles.methods"),
          type: "METHODS",
          break: false,
          content: "",
        },
      };
    }

    let markText = "";

    props.data.methods?.forEach((item, index) => {
      markText += `#### ${item.code}\n`;
      markText += `<span style="font-size: 0.75em;">${item.name}</span>\n`;

      item.metadata?.forEach((field: any) => {
        if (field.type === "RANGE") {
          markText += `- **${field.name}:**`;
          if (field.min) {
            markText += ` &nbsp; ${field.min}`;
          }
          if (field.max) {
            markText += ` a ${field.max}`;
          }
          if (field.measure) {
            markText += ` ${field.measure}`;
          }
          markText += `\n`;
        } else {
          if (
            field.answer !== null &&
            field.answer !== undefined &&
            field.answer !== ""
          ) {
            markText += `- **${field.name}:** &nbsp; ${field.answer}\n`;
          }
        }
      });

      if ((props.data.methods?.length ?? 0) > index + 1) {
        markText += "---\n";
      }
    });

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 6;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.methods!.content = marked(methodText);
        }
        newPage("methods", "METHODS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.methods!.content = marked(methodText);
    }
  };

  const paginateForm = async () => {
    if (!props.data?.forms?.length && !props.data?.methods?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("forms", "FORMS", t("report.titles.field_memory"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        forms: {
          title: t("report.titles.field_memory"),
          type: "FORMS",
          break: false,
          content: "",
        },
      };
    }

    let markText = "";

    const forms = props.data.forms ?? [];
    const methods = props.data.methods ?? [];
    const answers = props.data.answers;

    if (props.filters?.includes("form_question")) {
      for (const [index, form] of forms.entries()) {
        markText += `### ${form.code.toUpperCase()} &nbsp; | &nbsp; ${form.name.toUpperCase()} \n`;
        markText += `<div style="font-size: 0.75em;">${form.description ?? ""}</div>\n`;

        const questions = form.questions ?? [];
        const formAnswer = answers?.metadata?.form[form.form_id] ?? {};
        const orders = form.metadata?.orders ?? [];

        for (const id of orders) {
          const question = questions.find((q) => q.question_id === id);

          if (!question) continue;

          if (question.type === "TITLE") {
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${question.name?.toUpperCase()}</div>\n`;
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${question.description ?? ""}</div>\n\n`;
          }

          if (question.type === "INPUT" || question.type === "TEXTAREA") {
            if (
              formAnswer[id] === null ||
              formAnswer[id] === undefined ||
              formAnswer[id] === ""
            ) {
              continue;
            }

            markText += `- **${question.name}:** &nbsp; ${formAnswer[id] ?? ""}\n`;
          }

          if (question.type === "CHECKBOX") {
            const checkboxes = formAnswer[id]?.filter(
              (answer: any) => answer.value === true,
            );

            if (checkboxes?.length) {
              markText += `- **${question.name}:** &nbsp;\n`;
              checkboxes?.forEach((answer: any) => {
                markText += `  - ${answer.option}\n`;
              });
            }
          }

          if (question.type === "RADIO") {
            if (
              formAnswer[id] === null ||
              formAnswer[id] === undefined ||
              formAnswer[id] === ""
            ) {
              continue;
            }

            markText += `- **${question.name}:** &nbsp;`;
            const radioAnswer = question.metadata?.options?.find(
              (option: any) => option.value === formAnswer[id],
            );
            markText += `${radioAnswer?.option ?? ""}\n`;
          }

          if (question.type === "PHOTO") {
            const photoAnswer = formAnswer[id];
            if (photoAnswer?.file_id) {
              markText += "\n&nbsp;\n";
              markText += `#### ${question.name.toUpperCase()}\n`;
              markText += `${question.description ?? ""}\n\n`;
              const response = await fileGet(photoAnswer.file_id);
              const { success, data } = response;
              if (success) {
                markText += `<img src="${data.infos},${data.base64}" style="width: auto; height: 200px;" />\n\n`;
              }
            }
          }
        }

        if (forms.length > index + 1) {
          markText += "---\n";
        }
      }
    }

    if (props.filters?.includes("method_question")) {
      if (methods.length) {
        markText += "---\n\n";
      }

      methods.forEach((method, index) => {
        markText += `### ${method.code.toUpperCase()} &nbsp; | &nbsp; ${method.name.toUpperCase()} \n`;
        const methodAnswers =
          answers?.metadata?.method?.[method.method_id] ?? {};

        method.questions?.forEach((item: any) => {
          const methodAnswer = methodAnswers?.[item.question_id];

          if (item.type === "TITLE") {
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${item.name?.toUpperCase()}</div>\n`;
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${item.description ?? ""}</div>\n\n`;
          }

          if (item.type === "INPUT" || item.type === "TEXTAREA") {
            markText += `- **${item.name}:**  ${methodAnswer ?? ""}\n`;
          }

          if (item.type === "CHECKBOX") {
            markText += `- **${item.name}:** &nbsp;\n`;
            methodAnswer
              ?.filter((answer: any) => !!answer.value)
              ?.forEach((answer: any) => {
                markText += `  - ${answer.option}\n`;
              });
          }

          if (item.type === "RADIO") {
            markText += `- **${item.name}:** &nbsp;`;
            const radioAnswer = item.metadata?.options?.find(
              (option: any) => option.value === methodAnswer,
            );
            markText += `${radioAnswer?.option ?? ""}\n`;
          }
        });

        if (methods.length > index + 1) {
          markText += "\n";
          markText += "---\n";
          markText += "\n";
        }
      });
    }

    let methodText = "";
    markText.split("\n").forEach((text) => {
      // Detectar se a linha contém uma imagem
      const hasImage = text.includes("<img") && text.includes("height:");
      let blockHeight = 0;

      if (hasImage) {
        // Extrair altura da imagem do style
        const heightMatch = text.match(/height:\s*(\d+)px/);
        const imageHeight = heightMatch ? parseInt(heightMatch[1]) : 200;
        // Adicionar margem extra para título e descrição da imagem
        blockHeight = imageHeight + 40; // 60px para título + descrição + margens
      } else {
        const pragraph = getMarkdownBlockHeights(text, {
          width: "617px",
          font_size: "0.75em",
          line_height: "normal",
        });
        blockHeight = (pragraph[0]?.height ?? 0) + 7;
      }

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.forms!.content = marked(methodText);
        }
        newPage("forms", "FORMS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.forms!.content = marked(methodText);
    }
  };

  const paginateSamples = () => {
    if (!props.data?.samples?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("samples", "SAMPLES", t("report.titles.samples"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        samples: {
          title: t("report.titles.samples"),
          type: "SAMPLES",
          break: false,
          content: "",
        },
      };
    }

    const samples = props.data.samples;

    let markText = "";

    for (const [index, sample] of samples.entries()) {
      markText += `- **${t("report.labels.name")}:** &nbsp; ${sample.name}\n`;
      markText += `- **${t("report.labels.code")}:** &nbsp; ${sample.code}\n`;
      if (sample.method) {
        markText += `- **${t("report.labels.method")}:** &nbsp;`;
        markText += ` ${sample.method.code} &nbsp; | &nbsp; ${sample.method.name}\n`;
      } else {
        markText += "\n";
      }
      markText += `- **${t("report.labels.validity_date")}:** &nbsp; ${getDate(sample.validity)}\n`;
      markText += `- **${t("report.labels.description")}:** &nbsp; ${sample.description ?? ""}\n`;

      if (samples.length > index + 1) {
        markText += "---\n";
      }
    }

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 5;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.samples!.content = marked(methodText);
        }
        newPage("samples", "SAMPLES", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.samples!.content = marked(methodText);
    }
  };

  const paginateEquipments = () => {
    if (!props.data?.equipments?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("equipments", "EQUIPMENTS", t("report.titles.equipments"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        equipments: {
          title: t("report.titles.equipments"),
          type: "EQUIPMENTS",
          break: false,
          content: "",
        },
      };
    }

    const equipments = props.data.equipments;

    let markText = "";

    equipments?.forEach((item, index) => {
      markText += `<div style="display: flex; flex-direction: row; gap: 2em; align-items: flex-start; margin-top: 0.5em;">`;
      markText += `<div style="width: 100px; height: auto; background: #f5f5f5;">`;
      if (item.avatar) {
        markText += `<img src="${item.avatar.base64}" style="width: 100%; height: auto;" />`;
      }
      markText += `</div>`;

      markText += `<div style="display: flex; flex-direction: row; gap: 2em;">`;
      markText += `<ul style="list-style-type: none;">`;
      markText += `<li><strong>${t("report.labels.code")}:</strong> ${item.code}</li>`;
      markText += `<li><strong>${t("report.labels.name")}:</strong> ${item.name}</li>`;
      markText += `<li><strong>${t("report.labels.model")}:</strong> ${item.model}</li>`;
      markText += `<li><strong>${t("report.labels.brand")}:</strong> ${item.brand}</li>`;
      markText += `</ul>`;

      markText += `<ul style="list-style-type: none;">`;
      markText += `<li><strong>${t("report.labels.serial_number")}:</strong> ${item.serial_number}</li>`;
      markText += `<li><strong>${t("report.labels.fabrication_date")}:</strong> ${getDate(item.fabrication_date)}</li>`;
      markText += `<li><strong>${t("report.labels.validity_date")}:</strong> ${getDate(item.expired_date)}</li>`;
      markText += `</ul>`;
      markText += `</div>`;
      markText += `</div>\n`;

      if (equipments.length > index + 1) {
        markText += "<hr>\n";
      }
    });

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 6;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.equipments!.content = marked(methodText);
        }
        newPage("equipments", "EQUIPMENTS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.equipments!.content = marked(methodText);
    }
  };

  const paginateSigns = () => {
    if (!props.data?.signatures?.length) return;

    const SIGN_SIZE = 140; // Altura de cada assinatura
    const SIGN_GAP = 25; // Espaço entre assinaturas

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + SIGN_SIZE)) {
      newPage("signatures", "SIGNATURES", t("report.titles.signatures"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        signatures: {
          title: t("report.titles.signatures"),
          type: "SIGNATURES",
          break: false,
          content: [],
        },
      };
    }

    const signs = props.data?.signatures ?? [];

    let SIGNATURES: any[] = [];
    let ITEMS = 0;

    signs.forEach((sign, index) => {
      ITEMS++;

      if (ITEMS === 1) {
        // Primeira assinatura da linha
        if (!hasSpaceForContent(SIGN_SIZE)) {
          // Não há espaço, salvar assinaturas anteriores e criar nova página
          if (SIGNATURES.length > 0) {
            pages.value[PAGE]!.content!.signatures!.content = [...SIGNATURES];
          }
          newPage("signatures", "SIGNATURES", null);
          SIGNATURES = [sign];
          HEIGHT_INTERNAL += SIGN_SIZE;
        } else {
          SIGNATURES.push(sign);
          HEIGHT_INTERNAL += SIGN_SIZE;
        }
      } else if (ITEMS === 2) {
        // Segunda assinatura da linha
        SIGNATURES.push(sign);

        // Se não for a última, adicionar gap para próxima linha
        if (signs.length > index + 1) {
          HEIGHT_INTERNAL += SIGN_GAP;
          ITEMS = 0; // Resetar contador para próxima linha
        }
      }
    });

    // Salvar assinaturas restantes
    if (SIGNATURES.length > 0) {
      pages.value[PAGE].content!.signatures!.content = [...SIGNATURES];
    }
  };

  // Sempre adicionar título da primeira página
  paginateTitle();

  // paginate company
  if (props.filters?.includes("company")) {
    paginateCompany();
  }

  // paginate analyse
  if (props.filters?.includes("analyse")) {
    paginateAnalyse();
  }

  // paginate employee
  if (props.filters?.includes("employee")) {
    paginateEmployee();
  }

  // paginate methods
  if (props.filters?.includes("method")) {
    paginateMethod();
  }

  // paginate field memory
  if (
    props.filters?.includes("form_question") ||
    props.filters?.includes("method_question")
  ) {
    await paginateForm();
  }

  // paginate samples
  if (props.filters?.includes("sample")) {
    paginateSamples();
  }

  // paginate equipments
  if (props.filters?.includes("equipment")) {
    paginateEquipments();
  }

  // paginate signatures
  if (props.filters?.includes("signature")) {
    paginateSigns();
  }
}

watch(
  () => props.data,
  () => paginate(),
);

onMounted(() => {
  paginate();
});
</script>

<template>
  <template v-for="page in pages" :key="page.page">
    <page-export
      :width="width"
      :page="page.page + 1"
      :scale="scale"
      :show-footer="footer?.visible"
      :footer-title="footer?.title"
      :footer-subtitle="footer?.subtitle"
    >
      <div class="page-body">
        <template
          v-for="(item, index) of Object.values(page.content!)"
          :key="index"
        >
          <template v-if="item!.title">
            <div class="page-title" v-html="item!.title" />
          </template>

          <template v-if="item!.type === 'COMPANY'">
            <div class="page-content">
              <div class="company">
                <template v-if="item!.content.avatar">
                  <div class="brand">
                    <img :src="item!.content.avatar.base64" />
                  </div>
                </template>
                <div class="infos">
                  <template v-if="item!.content.name">
                    <div class="name">
                      {{ item!.content.name }}
                    </div>
                  </template>
                  <template v-if="item!.content.document">
                    <div class="document">
                      {{ item!.content.document }}
                    </div>
                  </template>
                  <template v-if="item!.content.address">
                    <div class="address">
                      {{ item!.content.address }}
                    </div>
                  </template>
                  <template v-if="item!.content.contact">
                    <div class="address">
                      {{ item!.content.contact }}
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'ANALYSE'">
            <div class="page-content">
              <div class="analyse">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'EMPLOYEE'">
            <div class="page-content">
              <div class="employee">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'METHODS'">
            <div class="page-content">
              <div class="methods">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'FORMS'">
            <div class="page-content">
              <div class="forms">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'SAMPLES'">
            <div class="page-content">
              <div class="samples">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'EQUIPMENTS'">
            <div class="page-content">
              <div class="equipments">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'SIGNATURES'">
            <div class="page-content">
              <div class="signatures">
                <template
                  v-for="sign in item!.content"
                  :key="sign.signature_id"
                >
                  <div class="signature">
                    <div class="image">
                      <template v-if="sign.signature">
                        <img :src="sign.signature" alt="Signature" />
                      </template>
                      <template v-else>
                        <img
                          :src="require('@/assets/images/signature.png')"
                          style="opacity: 0.25"
                        />
                      </template>
                    </div>
                    <div class="name">{{ sign.name }}</div>
                    <div class="type">
                      <template v-if="sign.type === 'OTHER'">
                        {{ sign.type_name }}
                      </template>
                      <template v-else>
                        {{ t(`common.labels.${sign.type.toLowerCase()}`) }}
                      </template>
                    </div>
                    <div class="date">{{ sign.date }}</div>
                    <div class="document">
                      {{ sign.document_name }} {{ sign.document_number }}
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </template>
      </div>
    </page-export>
  </template>
</template>

<style lang="scss" scoped>
.page-body {
  display: flex;
  flex-direction: column;
  margin-block-start: 2em;
  margin-inline: 2em;
  background: white;

  .page-title {
    display: flex;
    align-items: center;
    width: inherit;
    height: 2em;
    max-height: 2em;
    padding-inline-start: 1em;
    margin-block-end: 0.75em;
    background: var(--white-off);
    color: black;
    font-size: 0.875em;
    font-weight: bold;
    text-transform: uppercase;
  }

  .page-content {
    margin-block-end: 1.5em;
    padding-inline: 1em;
  }

  .company {
    display: flex;
    flex-direction: row;
    gap: 1em;

    .brand {
      min-width: 5em;
      width: 5em;
      height: 5em;

      img {
        width: 100%;
        height: auto;
      }
    }

    .infos {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.2rem;

      .name {
        font-size: 0.875em;
      }

      .document {
        font-size: 0.75em;
        font-weight: bold;
      }

      .address {
        font-size: 0.75em;
        text-transform: uppercase;
      }
    }

    h2 {
      img {
        width: auto;
        height: 100px;
      }
    }
  }

  .analyse,
  .employee,
  .methods,
  .equipments,
  .samples,
  .forms {
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    font-size: 0.75em;
    line-height: 1.4;
  }

  .signatures {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5em;

    .signature {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25em;
      font-size: 0.75em;
      margin-top: 1em;

      .image {
        display: flex;
        align-items: center;
        justify-items: center;
        width: 100%;

        img {
          width: auto;
          height: 40px;
          margin-inline: auto;
        }
      }

      .name {
        width: 100%;
        font-weight: bold;
        border-top: 1px solid var(--border);
        text-align: center;
        padding-top: 0.5em;
      }
    }
  }

  .photo {
    width: auto;
    height: 150px;
  }
}
</style>
